import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  FormFeedback, FormGroup, FormText, Input, Label,
} from 'reactstrap';
import isFloat from 'validator/es/lib/isFloat';

import {
  convertEnergyPricePartnerToPowertracer,
} from 'src/util/conversions';
import { useIntlCurrency } from 'src/util/i18n/currency';
import getInitialPriceDisplayValue from 'src/util/price';

/**
 * InputPrice component for entering energy prices in locale-dependent Partner format.
 * Partner format varies by locale (e.g., c/kWh in Australia, yen/kWh in Japan,
 * pence/kWh in England). Converts user input to Powertracer format ($/Wh) using
 * Big.js for precision. The conversion is handled by `convertEnergyPricePartnerToPowertracer()`
 * which is locale-aware via the `intlCurrency` parameter.
 * @param {object} props component props.
 * @param {number|null} props.defaultValue initial price in Powertracer format ($/Wh).
 * @param {Function} props.handlePriceChangeFunc callback receiving Big.js object or null.
 * @param {string} props.helpText help text displayed below input.
 * @param {string} props.idPrefix prefix for input element ID.
 * @param {string} props.label label for the input field.
 * @param {boolean|null} props.valid validation state.
 * @returns {React.ReactElement} The InputPrice component.
 */
function InputPrice({
  defaultValue = null,
  handlePriceChangeFunc,
  helpText = '',
  idPrefix,
  label = 'Price',
  valid = null,
}) {
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  // Dual-state pattern for price:
  // - displayValue: String for input field, preserves user formatting (e.g., "1.0", "0.")
  // - Price submitted to parent via handlePriceChangeFunc is a Big.js object
  const [displayValue, setDisplayValue] = useState(
    () => getInitialPriceDisplayValue(intlCurrency, defaultValue),
  );

  const handleChange = (event) => {
    const { value } = event.target;

    // Store raw string for display to preserve trailing zeros and decimal points
    setDisplayValue(value);

    // Convert to Big.js in Powertracer format for mutation submission
    // Pass null for invalid input to signal validation failure
    const priceBig = isFloat(value)
      ? convertEnergyPricePartnerToPowertracer(intlCurrency, parseFloat(value))
      : null;

    handlePriceChangeFunc(priceBig);
  };

  const invalidPriceFeedback = intl.formatMessage({
    id: 'trade_rule.time_of_use.input_price.form_feedback.invalid',
    defaultMessage: 'Invalid price',
  });

  return (
    <FormGroup>
      <Label for={`${idPrefix}Price`}>{label}</Label>
      <Input
        type="number"
        name="price"
        id={`${idPrefix}Price`}
        value={displayValue}
        onChange={handleChange}
        step="any"
        valid={valid !== null && valid}
        invalid={valid !== null && !valid}
      />
      <FormFeedback>{invalidPriceFeedback}</FormFeedback>
      <FormText>{helpText}</FormText>
    </FormGroup>
  );
}

InputPrice.propTypes = {
  defaultValue: PropTypes.number,
  handlePriceChangeFunc: PropTypes.func.isRequired,
  helpText: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
      ]),
    ), PropTypes.string, PropTypes.element]),
  idPrefix: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
  ]),
  valid: PropTypes.bool,
};

export default InputPrice;
